import { isNodeSelection, type Editor } from "@tiptap/react"
import * as React from "react"

// --- Hooks ---
import { useTiptapEditor } from "@/hooks/use-tiptap-editor"

// --- Icons ---
import { LinkIcon } from "@/tiptap/tiptap-icons/link-icon"

// --- Lib ---
import { isMarkInSchema } from "@/tiptap/lib/tiptap-utils"

// --- UI Primitives ---
import type { ButtonProps } from "@/tiptap/tiptap-ui-primitive/button"
import { Button } from "@/tiptap/tiptap-ui-primitive/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/tiptap/tiptap-ui-primitive/popover"

// --- Styles ---
import "@/tiptap/tiptap-ui/link-popover/link-popover.scss"

// --- Components ---
import { LinkEditor } from "@/tiptap/tiptap-ui/link-popover/link-editor"

export interface LinkHandlerProps {
  editor: Editor | null
  onSetLink?: () => void
  onLinkActive?: () => void
}

export interface LinkMainProps {
  url: string
  setUrl: React.Dispatch<React.SetStateAction<string | null>>
  setLink: (url?: string, title?: string) => void
  removeLink: () => void
  editor?: Editor | null
}

export const useLinkHandler = (props: LinkHandlerProps) => {
  const { editor, onSetLink, onLinkActive } = props
  const [url, setUrl] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!editor) return

    // Get URL immediately on mount
    const { href } = editor.getAttributes("link")

    if (editor.isActive("link") && url === null) {
      setUrl(href || "")
      onLinkActive?.()
    }
  }, [editor, onLinkActive, url])

  React.useEffect(() => {
    if (!editor) return

    const updateLinkState = () => {
      const { href } = editor.getAttributes("link")
      setUrl(href || "")

      if (editor.isActive("link") && url !== null) {
        onLinkActive?.()
      }
    }

    editor.on("selectionUpdate", updateLinkState)
    return () => {
      editor.off("selectionUpdate", updateLinkState)
    }
  }, [editor, onLinkActive, url])

  const setLink = React.useCallback((newUrl?: string, title?: string) => {
    const linkUrl = newUrl || url
    if (!linkUrl || !editor) return

    const { from, to } = editor.state.selection
    const hasSelection = from !== to
    
    // If title is provided and different from link text, update the text
    if (title && hasSelection) {
      const currentText = editor.state.doc.textBetween(from, to, ' ')
      
      if (title !== currentText) {
        // Update both text and link
        editor.chain()
          .focus()
          .deleteRange({ from, to })
          .insertContent({
            type: 'text',
            text: title,
            marks: [{ type: 'link', attrs: { href: linkUrl } }]
          })
          .run()
      } else {
        // Just update the link
        editor.chain().focus().extendMarkRange("link").setLink({ href: linkUrl }).run()
      }
    } else if (hasSelection) {
      // Just update the link on selected text
      editor.chain().focus().extendMarkRange("link").setLink({ href: linkUrl }).run()
    } else if (title) {
      // No selection, insert new linked text
      editor.chain()
        .focus()
        .insertContent([
          {
            type: 'text',
            text: title,
            marks: [{ type: 'link', attrs: { href: linkUrl } }]
          },
          {
            type: 'text',
            text: ' ',
            marks: []
          }
        ])
        .run()
    }

    setUrl(null)

    onSetLink?.()
  }, [editor, onSetLink, url])

  const removeLink = React.useCallback(() => {
    if (!editor) return
    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .unsetLink()
      .setMeta("preventAutolink", true)
      .run()
    setUrl("")
  }, [editor])

  return {
    url: url || "",
    setUrl,
    setLink,
    removeLink,
    isActive: editor?.isActive("link") || false,
  }
}

export const LinkButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <Button
        type="button"
        className={className}
        data-style="ghost"
        role="button"
        tabIndex={-1}
        aria-label="Link"
        tooltip="Link"
        ref={ref}
        {...props}
      >
        {children || <LinkIcon className="tiptap-button-icon" />}
      </Button>
    )
  }
)

export const LinkContent: React.FC<{
  editor?: Editor | null
}> = ({ editor: providedEditor }) => {
  const { editor } = useTiptapEditor(providedEditor)

  const linkHandler = useLinkHandler({
    editor: editor,
  })

  return <LinkMain {...linkHandler} editor={editor} />
}

const LinkMain: React.FC<LinkMainProps> = ({
  url,
  setUrl,
  setLink,
  removeLink,
  editor,
}) => {
  if (!editor) {
    return null
  }

  return (
    <LinkEditor 
      url={url}
      setUrl={(newUrl) => setUrl(newUrl)}
      onSave={setLink}
      onRemove={removeLink}
      editor={editor}
    />
  )
}

export interface LinkPopoverProps extends Omit<ButtonProps, "type"> {
  /**
   * The TipTap editor instance.
   */
  editor?: Editor | null
  /**
   * Whether to hide the link popover.
   * @default false
   */
  hideWhenUnavailable?: boolean
  /**
   * Callback for when the popover opens or closes.
   */
  onOpenChange?: (isOpen: boolean) => void
  /**
   * Whether to automatically open the popover when a link is active.
   * @default true
   */
  autoOpenOnLinkActive?: boolean
}

export function LinkPopover({
  editor: providedEditor,
  hideWhenUnavailable = false,
  onOpenChange,
  autoOpenOnLinkActive = true,
  ...props
}: LinkPopoverProps) {
  const { editor } = useTiptapEditor(providedEditor)

  const linkInSchema = isMarkInSchema("link", editor)

  const [isOpen, setIsOpen] = React.useState(false)

  const onSetLink = () => {
    setIsOpen(false)
  }

  const onLinkActive = () => setIsOpen(autoOpenOnLinkActive)

  const linkHandler = useLinkHandler({
    editor: editor,
    onSetLink,
    onLinkActive,
  })

  // const isDisabled = React.useMemo(() => {
  //   if (!editor) return true
  //   if (editor.isActive("codeBlock")) return true
    
  //   // Debug logging
  //   let canSetLink = false
  //   try {
  //     // Check if setLink method exists and if we can call it
  //     if (editor.can().setLink) {
  //       canSetLink = editor.can().setLink({ href: "" })
  //     } else {
  //       console.warn('setLink method not found on editor.can()')
  //     }
  //   } catch (e) {
  //     console.error('Error checking canSetLink:', e)
  //   }
    
  //   console.log('LinkPopover isDisabled check:', {
  //     editor: !!editor,
  //     isEditable: editor.isEditable,
  //     isCodeBlock: editor.isActive("codeBlock"),
  //     canSetLink: canSetLink,
  //     hasSetLinkMethod: !!editor.can().setLink,
  //     selection: {
  //       empty: editor.state.selection.empty,
  //       from: editor.state.selection.from,
  //       to: editor.state.selection.to,
  //       text: editor.state.doc.textBetween(editor.state.selection.from, editor.state.selection.to, ' ')
  //     }
  //   })
    
  //   return !canSetLink
  // }, [editor?.state])
  const isDisabled = false

  const canSetLink = React.useMemo(() => {
    if (!editor) return false
    try {
      const result = editor.can().setMark("link")
      console.log('LinkPopover canSetLink check:', {
        canSetMark: result,
        linkMarkExists: !!editor.schema.marks.link,
        selection: editor.state.selection
      })
      return result
    } catch (e) {
      console.error('LinkPopover canSetLink error:', e)
      return false
    }
  }, [editor])

  const isActive = editor?.isActive("link") ?? false

  const handleOnOpenChange = React.useCallback(
    (nextIsOpen: boolean) => {
      setIsOpen(nextIsOpen)
      onOpenChange?.(nextIsOpen)
    },
    [onOpenChange]
  )

  const show = React.useMemo(() => {
    if (!linkInSchema || !editor) {
      return false
    }

    if (hideWhenUnavailable) {
      if (isNodeSelection(editor.state.selection) || !canSetLink) {
        return false
      }
    }

    return true
  }, [linkInSchema, hideWhenUnavailable, editor, canSetLink])

  if (!show || !editor || !editor.isEditable) {
    return null
  }

  console.log("isDisabled", isDisabled, isActive, canSetLink, show, editor)

  return (
    <Popover open={isOpen} onOpenChange={handleOnOpenChange}>
      <PopoverTrigger asChild>
        <LinkButton
          disabled={isDisabled}
          data-active-state={isActive ? "on" : "off"}
          data-disabled={isDisabled}
          onClick={() => {
            console.log('LinkButton clicked:', {
              isDisabled,
              isActive,
              canSetLink,
              show,
              editor: {
                isEditable: editor?.isEditable,
                selection: editor?.state.selection,
                canSetLink: editor?.can().setLink ? editor.can().setLink({ href: "" }) : 'method not found',
                canSetMark: editor?.can().setMark ? editor.can().setMark("link") : 'method not found'
              }
            })
          }}
          {...props}
        />
      </PopoverTrigger>

      <PopoverContent 
        className="link-popover-content p-0 w-auto"
        sideOffset={5}
        alignOffset={0}
        side="bottom"
        align="start"
      >
        <LinkMain {...linkHandler} editor={editor} />
      </PopoverContent>
    </Popover>
  )
}

LinkButton.displayName = "LinkButton"
